import { useEffect, useRef } from "react";
import { FeedbackPageContextGetter, useFeedbackApi } from "./FeedbackContext";

/**
 * Contribute page-scoped keys to feedback reports while the calling
 * component is mounted. The getter is read lazily at submit time, and the
 * latest render's getter is always the one invoked — callers can pass an
 * inline closure over fresh state without worrying about identity.
 *
 * Registered getters merge (see `collectPageContext`), so shared components
 * and the page can contribute simultaneously. Outside a FeedbackProvider
 * (component tests, storybook) this is a no-op rather than a crash — shared
 * building blocks like the context selector register unconditionally.
 */
export function useRegisterFeedbackContext(
  getter: FeedbackPageContextGetter
): void {
  // The stable API context only — registering must not subscribe the
  // (potentially heavy) caller to modal open/close state changes.
  const registerPageContext = useFeedbackApi()?.registerPageContext;
  const getterRef = useRef(getter);
  getterRef.current = getter;

  useEffect(() => {
    if (!registerPageContext) return;
    return registerPageContext(() => getterRef.current());
  }, [registerPageContext]);
}
